import { useRef } from "react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { U } from "@/lib/images";

const SERVICES = [
    {
        id: "drywall",
        n: "01",
        title: "Drywall & Prep",
        img: U("photo-1776821456681-305bdcd81096", 1400),
        alt: "Bright empty room prepped for drywall finishing",
        desc: "Flawless walls are the foundation of every finish we apply. We hang, tape, float and sand to a true Level 5 surface — then texture, patch or rebuild to match what is already there.",
        specs: ["Level 5 smooth finish", "Patch & repair", "Textures & matching", "Moisture-resistant board"],
        span: "md:col-span-7",
    },
    {
        id: "paint",
        n: "02",
        title: "Architectural Painting",
        img: U("photo-1689102816270-dc301aeaec35", 1400),
        alt: "Paint can and brush resting on wooden boards",
        desc: "Crisp lines, rich color and coatings that stand up to Florida sun and humidity — from a single accent wall to a full interior repaint.",
        specs: ["Interior repaints", "Premium low-VOC coatings", "Venetian plaster", "Color consultation"],
        span: "md:col-span-5",
    },
    {
        id: "tile",
        n: "03",
        title: "Custom Tile & Marble",
        img: U("photo-1682888818704-6dc91e9d7532", 1400),
        alt: "Marble tiled bathroom with green vanity",
        desc: "Showers, floors, backsplashes and feature walls — set level, grouted tight, in porcelain, natural stone and marble.",
        specs: ["Showers & wet rooms", "Kitchen backsplashes", "Large-format tile", "Marble & natural stone"],
        span: "md:col-span-5",
    },
    {
        id: "flooring",
        n: "04",
        title: "Hardwood & Flooring",
        img: U("photo-1692313209122-26d8f28bb560", 1400),
        alt: "Stacked hardwood floor planks ready for installation",
        desc: "Solid and engineered hardwood, luxury vinyl plank and refinishing — flat subfloors, tight seams and glass-smooth finishes.",
        specs: ["Solid & engineered hardwood", "Luxury vinyl plank (LVP)", "Refinishing & resanding", "Stairs & transitions"],
        span: "md:col-span-7",
    },
];

const ServiceCard = ({ s }) => {
    const ref = useRef(null);

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
    };

    return (
        <article
            ref={ref}
            data-testid={`service-card-${s.id}`}
            onMouseMove={onMove}
            className={`group relative col-span-1 flex h-full flex-col overflow-hidden bg-white shadow-lift ${s.span}`}
        >
            <div className="relative aspect-[16/10] overflow-hidden">
                <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <div
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                        background:
                            "radial-gradient(420px circle at var(--mx, 50%) var(--my, 50%), rgba(217,155,83,0.25), transparent 65%)",
                    }}
                />
                <span className="absolute left-5 top-5 bg-bone/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-espresso backdrop-blur-sm">
                    {s.n}
                </span>
            </div>
            <div className="flex flex-1 flex-col p-7 md:p-9">
                <h3 className="font-serif text-2xl font-medium text-espresso md:text-3xl">
                    {s.title}
                </h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-clay md:text-base">
                    {s.desc}
                </p>
                <ul className="mt-6 grid grid-cols-1 gap-2 border-t border-line pt-5 sm:grid-cols-2">
                    {s.specs.map((spec) => (
                        <li
                            key={spec}
                            className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-fog"
                        >
                            <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-oak" />
                            {spec}
                        </li>
                    ))}
                </ul>
            </div>
        </article>
    );
};

export const Services = () => (
    <section id="services" className="scroll-mt-20 bg-bone py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <Eyebrow>What we do</Eyebrow>
                    <h2 className="mt-5 max-w-xl font-serif text-2xl font-normal leading-[1.15] tracking-tight text-espresso sm:text-3xl md:text-4xl">
                        Four trades. One standard:
                        <span className="italic text-oak"> flawless.</span>
                    </h2>
                </div>
                <p className="max-w-sm text-sm leading-relaxed text-clay md:text-base">
                    Every surface we touch gets the same obsession — whether it is
                    one patch in a hallway or a whole-house refresh.
                </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
                {SERVICES.map((s, i) => (
                    <Reveal key={s.id} delay={0.08 * i} className={`${s.span} col-span-1 flex`}>
                        <ServiceCard s={s} />
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
